/**
 * /v1 parish QR codes (Phase 10).
 * Backend select: id, label, description, status, payloadUrl, validUntil, basketId, createdAt, updatedAt.
 */

export type QrStatus = 'ACTIVE' | 'INACTIVE';

export type ApiQrCode = {
    id: string;
    label: string;
    description: string | null;
    status: QrStatus;
    payloadUrl: string;
    validUntil: string | null;
    /** Donation target. Nullable only for legacy rows retired backend-side — they 404 on scan. */
    basketId: string | null;
    createdAt: string;
    updatedAt: string;
};

/** UI row — payloadUrl is the only QR canvas value (never client-built). */
export type QrCode = {
    id: string;
    label: string;
    description: string;
    status: QrStatus;
    payloadUrl: string;
    validUntil: string | null;
    basketId: string | null;
    createdAt: string;
    updatedAt: string;
    raw: ApiQrCode;
};

export type CreateQrPayload = {
    label: string;
    /**
     * Required by the backend (`createQrSchema`) — a code with no target 404s on every scan.
     * Required here too, so an omission is a compile error rather than a runtime 400.
     */
    basketId: string;
    description?: string;
    validUntil?: string;
};

/**
 * Whether a code will actually resolve when scanned.
 *
 * `status: 'ACTIVE'` is not sufficient. Expiry is enforced read-time and never flips the status,
 * so an expired code stays ACTIVE and still comes back from `?status=ACTIVE` — but `resolve()`
 * filters `validUntil > now` and 404s it. Rendering one produces a QR that looks fine, prints
 * fine, and scans to nothing.
 *
 * An absent or unparseable `validUntil` means "never expires" — the overwhelmingly common case
 * (this console never sets one) and the safe reading, since treating a live code as dead only
 * hides a working QR.
 */
export function isQrCodeLive(qr: Pick<QrCode, 'validUntil'>): boolean {
    if (!qr.validUntil) return true;
    const expiresAt = new Date(qr.validUntil).getTime();
    return Number.isNaN(expiresAt) || expiresAt > Date.now();
}

export function normalizeQrCode(row: ApiQrCode): QrCode {
    return {
        id: row.id,
        label: row.label || '',
        description: row.description || '',
        status: row.status,
        payloadUrl: row.payloadUrl || '',
        validUntil: row.validUntil ?? null,
        basketId: row.basketId ?? null,
        createdAt: row.createdAt || '',
        updatedAt: row.updatedAt || '',
        raw: row,
    };
}
